/**
 * The inbox: one row per conversation, newest activity first, and the two unread figures the
 * header says („3 nieprzeczytane wiadomości w 2 rozmowach").
 *
 * Unread is the SERVER's count per conversation (`unreadCount`); there is no total endpoint, so the
 * total is their sum — the mobile app's `useUnreadBadge`. A conversation the trainer has open is
 * zeroed by the read mark, not by this module.
 */

import type {
  TrainWithMeModulesChatDtosResponsesConversationResponse as ConversationResponse,
} from '~/shared/api/generated/model';

export type PreviewKind =
  | 'text'
  | 'image'
  | 'video'
  | 'voice'
  | 'exercise'
  | 'plan'
  | 'summary'
  | 'clipRequest'
  | 'clip'
  | 'unsupported'
  | 'none';

export interface ConversationRow {
  readonly id: number;
  readonly name: string;
  readonly initials: string;
  readonly avatarUrl: string | null;
  readonly customerId: number | null;
  readonly unread: number;
  readonly canSend: boolean;
  /** The instant the row sorts and times by: the last message, else when the thread was opened. */
  readonly activityAt: string;
  readonly preview: {
    readonly kind: PreviewKind;
    /** The text of a text message, the plan's name for a plan card; empty otherwise. */
    readonly text: string;
    readonly mine: boolean;
  };
}

const PREVIEW_BY_TYPE: Readonly<Record<string, PreviewKind>> = {
  Text: 'text',
  Image: 'image',
  Video: 'video',
  Voice: 'voice',
  ExerciseCard: 'exercise',
  TrainingPlan: 'plan',
  WorkoutSummary: 'summary',
  ExerciseClipRequest: 'clipRequest',
  ExerciseClip: 'clip',
};

export function toConversationRow(
  wire: ConversationResponse,
  myUserId: string | null,
): ConversationRow | null {
  if (typeof wire.id !== 'number') return null;
  const last = wire.lastMessage ?? null;
  const kind: PreviewKind =
    last === null ? 'none' : (PREVIEW_BY_TYPE[last.contentType ?? 'Text'] ?? 'unsupported');
  const name = wire.recipient?.fullName?.trim() ?? '';
  return {
    id: wire.id,
    name,
    initials: wire.recipient?.initials?.trim() ?? '',
    avatarUrl: wire.recipient?.avatarUrl ?? null,
    customerId: wire.recipientCustomerId ?? null,
    unread: Math.max(0, wire.unreadCount ?? 0),
    canSend: wire.canSendMessages ?? false,
    activityAt: last?.createdAt ?? wire.createdAt ?? new Date(0).toISOString(),
    preview: {
      kind,
      text: last?.text?.trim() ?? '',
      mine: last !== null && myUserId !== null && last.senderUserId === myUserId,
    },
  };
}

export function toConversationRows(
  wire: readonly ConversationResponse[] | undefined,
  myUserId: string | null,
): ConversationRow[] {
  return (wire ?? [])
    .map((conversation) => toConversationRow(conversation, myUserId))
    .filter((row): row is ConversationRow => row !== null)
    .sort((a, b) => Date.parse(b.activityAt) - Date.parse(a.activityAt) || b.id - a.id);
}

/** Lower case with the diacritics folded away, so „zosia" finds „Zośka" and „Łukasz" finds „lukasz". */
export function foldForSearch(value: string): string {
  return value
    .toLocaleLowerCase('pl')
    .replace(/ł/g, 'l')
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');
}

/** The rows a query keeps: a match on the client's name or on the last message's text. */
export function searchRows(rows: readonly ConversationRow[], query: string): ConversationRow[] {
  const needle = foldForSearch(query.trim());
  if (needle === '') return [...rows];
  return rows.filter(
    (row) => foldForSearch(row.name).includes(needle) || foldForSearch(row.preview.text).includes(needle),
  );
}

export interface UnreadTotals {
  readonly messages: number;
  readonly conversations: number;
}

export function unreadTotals(rows: readonly ConversationRow[]): UnreadTotals {
  let messages = 0;
  let conversations = 0;
  for (const row of rows) {
    if (row.unread <= 0) continue;
    messages += row.unread;
    conversations += 1;
  }
  return { messages, conversations };
}

/**
 * What the row's right edge says about time: the clock for today, „wczoraj", else the short date.
 * Returned as a shape so the caller words it.
 */
export type RowTime =
  | { readonly kind: 'clock'; readonly at: string }
  | { readonly kind: 'yesterday' }
  | { readonly kind: 'date'; readonly at: string };

export function rowTimeOf(iso: string, now: Date): RowTime {
  const at = new Date(iso);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(at, now)) return { kind: 'clock', at: iso };
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (sameDay(at, yesterday)) return { kind: 'yesterday' };
  return { kind: 'date', at: iso };
}

/** The conversation this client is in, if the inbox already holds one. */
export function conversationOfCustomer(
  rows: readonly ConversationRow[],
  customerId: number,
): ConversationRow | null {
  return rows.find((row) => row.customerId === customerId) ?? null;
}

/** The neighbouring conversation in the inbox's order, for `j` / `k`. Clamped at both ends. */
export function neighbourConversation(
  rows: readonly ConversationRow[],
  currentId: number | null,
  step: 1 | -1,
): number | null {
  if (rows.length === 0) return null;
  const index = rows.findIndex((row) => row.id === currentId);
  if (index === -1) return rows[0]?.id ?? null;
  const next = rows[Math.max(0, Math.min(rows.length - 1, index + step))];
  return next === undefined || next.id === currentId ? null : next.id;
}
